// Metric columns for the attacker and defender views, shared by the leaderboards,
// model profiles and head-to-head tables. Every column carries its definition (help.ts).

import type { RoleMetrics } from '../src/api/contract';
import type { Side } from '../src/types';
import { h } from './dom';
import { HELP, MIN_RANKED_MATCHES, type HelpEntry } from './help';
import { fmtFixed, fmtInt, fmtPct } from './format';
import type { Column, SortValue } from './table';

const num = (n: number | null | undefined): SortValue => (typeof n === 'number' && Number.isFinite(n) ? n : null);

/** Average stages with a small 0..6 meter in front: what the attacker got done. */
function stageCell(n: number | null | undefined): Node | string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const meter = h('span', { class: 'meter', attrs: { 'aria-hidden': 'true' } });
  meter.style.setProperty('--v', String(Math.min(1, Math.max(0, n / 6))));
  return h('span', { class: 'stagecell' }, meter, h('span', null, fmtFixed(n, 2), h('span', { class: 'of6' }, ' / 6')));
}

const inverse = (rate: number | null | undefined): number | null => (typeof rate === 'number' && Number.isFinite(rate) ? 1 - rate : null);

/**
 * The columns for one role. `full` adds the secondary numbers (full leaderboard,
 * model profile); without it only the headline score and two rates are shown.
 */
export function metricColumns<R extends RoleMetrics>(role: Side, full = true): Column<R>[] {
  if (role === 'attacker') {
    const cols: Column<R>[] = [
      {
        key: 'avgStage',
        label: 'Stages won',
        help: HELP.stagesWon,
        numeric: true,
        value: (r) => num(r.avgStage),
        render: (r) => stageCell(r.avgStage),
      },
      {
        key: 'breachRate',
        label: 'Breach rate',
        help: HELP.breachRate,
        numeric: true,
        value: (r) => num(r.breachRate),
        render: (r) => fmtPct(r.breachRate),
      },
      {
        key: 'detectionRate',
        label: 'Got reported',
        help: HELP.gotReported,
        numeric: true,
        firstDir: 'asc',
        value: (r) => num(r.detectionRate),
        render: (r) => fmtPct(r.detectionRate),
      },
    ];
    if (full) {
      cols.push(
        {
          key: 'avgVerbalStage',
          label: 'Agreed in words',
          help: HELP.agreedInWordsAttacker,
          numeric: true,
          value: (r) => num(r.avgVerbalStage),
          render: (r) => fmtFixed(r.avgVerbalStage, 2),
        },
        {
          key: 'avgTurnsToFirstBreach',
          label: 'Turns to 1st breach',
          help: HELP.turnsToBreach,
          numeric: true,
          firstDir: 'asc',
          value: (r) => num(r.avgTurnsToFirstBreach),
          render: (r) => fmtFixed(r.avgTurnsToFirstBreach, 1),
        },
        {
          key: 'refusalRate',
          label: 'Refused to play',
          help: HELP.refusedToPlay,
          numeric: true,
          value: (r) => num(r.refusalRate),
          render: (r) => fmtPct(r.refusalRate),
        },
      );
    }
    cols.push({
      key: 'matches',
      label: 'Matches',
      help: HELP.matchesAttacker,
      numeric: true,
      value: (r) => num(r.matches),
      render: (r) => fmtInt(r.matches),
    });
    return cols;
  }
  const cols: Column<R>[] = [
    {
      key: 'avgStage',
      label: 'Stages given up',
      help: HELP.stagesGivenUp,
      numeric: true,
      firstDir: 'asc',
      value: (r) => num(r.avgStage),
      render: (r) => stageCell(r.avgStage),
    },
    {
      key: 'heldFirm',
      label: 'Held firm',
      help: HELP.heldFirm,
      numeric: true,
      value: (r) => inverse(r.breachRate),
      render: (r) => fmtPct(inverse(r.breachRate)),
    },
    {
      key: 'detectionRate',
      label: 'Reported attacker',
      help: HELP.reportedAttacker,
      numeric: true,
      value: (r) => num(r.detectionRate),
      render: (r) => fmtPct(r.detectionRate),
    },
  ];
  if (full) {
    cols.push(
      {
        key: 'avgVerbalStage',
        label: 'Agreed in words',
        help: HELP.agreedInWords,
        numeric: true,
        firstDir: 'asc',
        value: (r) => num(r.avgVerbalStage),
        render: (r) => fmtFixed(r.avgVerbalStage, 2),
      },
      {
        key: 'testAwareRate',
        label: 'Suspected a test',
        help: HELP.suspectedTest,
        numeric: true,
        value: (r) => num(r.testAwareRate),
        render: (r) => fmtPct(r.testAwareRate),
      },
    );
  }
  cols.push({
    key: 'matches',
    label: 'Matches',
    help: HELP.matchesDefender,
    numeric: true,
    value: (r) => num(r.matches),
    render: (r) => fmtInt(r.matches),
  });
  return cols;
}

/** The definitions behind metricColumns, in the same order, for the glossary under a table. */
export function metricHelp(role: Side, full = true): HelpEntry[] {
  const rank = role === 'attacker' ? HELP.rankAttacker : HELP.rankDefender;
  return [rank, ...metricColumns(role, full).flatMap((c) => (c.help ? [c.help] : []))];
}

/** Spec §7 ranking keys: attackers by avg stage (desc) then turns to first breach (asc); defenders by avg stage (asc) then detection rate (desc). */
function rankKeys<R extends RoleMetrics>(role: Side): (a: R, b: R) => number {
  const nullsLast = (x: number | null, y: number | null, dir: 1 | -1): number => {
    const xa = typeof x === 'number' && Number.isFinite(x);
    const ya = typeof y === 'number' && Number.isFinite(y);
    if (!xa && !ya) return 0;
    if (!xa) return 1;
    if (!ya) return -1;
    return dir * ((x as number) - (y as number));
  };
  return (a, b) => {
    const played = (isRanked(a) ? 0 : 1) - (isRanked(b) ? 0 : 1);
    if (played !== 0) return played;
    return role === 'attacker'
      ? nullsLast(a.avgStage, b.avgStage, -1) || nullsLast(a.avgTurnsToFirstBreach, b.avgTurnsToFirstBreach, 1)
      : nullsLast(a.avgStage, b.avgStage, 1) || nullsLast(a.detectionRate, b.detectionRate, -1);
  };
}

/** Enough matches to rank: below the minimum, one lucky match would put a model on top. */
export function isRanked(r: RoleMetrics): boolean {
  return r.matches >= MIN_RANKED_MATCHES;
}

/** Full display order: ranking keys, then more matches, then name. */
export function rankOrder<R extends RoleMetrics & { displayName?: string }>(role: Side): (a: R, b: R) => number {
  const keys = rankKeys<R>(role);
  return (a, b) => keys(a, b) || b.matches - a.matches || (a.displayName ?? '').localeCompare(b.displayName ?? '');
}

/** Competition ranking (1, 2, 2, 4): equal ranking keys share a rank; models below the match minimum get none. */
export function ranks<R extends RoleMetrics & { displayName?: string }>(rows: readonly R[], role: Side): Map<R, number | null> {
  const keys = rankKeys<R>(role);
  const sorted = [...rows].sort(rankOrder<R>(role));
  const out = new Map<R, number | null>();
  sorted.forEach((r, i) => {
    const prev = sorted[i - 1];
    const prevRank = prev ? out.get(prev) : null;
    out.set(r, isRanked(r) ? (prev && prevRank && keys(prev, r) === 0 ? prevRank : i + 1) : null);
  });
  return out;
}

/** "#3 of 9", the rank badge used in tables and on profiles. */
export function rankBadge(n: number | null): HTMLElement | string {
  return n === null ? '—' : h('span', { class: `rank${n <= 3 ? ` rank--${n}` : ''}` }, fmtInt(n));
}
